// Who is this person, and which door is theirs — as a fact, never a guess.
//
// ─── 2026-09-25, OWNER: « IT'S LIKE THERE ARE TWO GATEWAYS » ────────────────
//
// An admin opened app.storiz.ma and got the CUSTOMER page (« Mes commandes »,
// « Erreur lors du chargement des commandes »). When GET /auth/me did not
// answer within 2.5 s or failed, useAuthStore INVENTED a profile —
// { role: 'customer', full_name: <email> } — so:
//   · the entry page showed the customer door, and
//   · every /admin page bounced him back to it (ProtectedRoute sends a
//     non-admin to « / »), which is why it was « always » the entry page.
// Server logs, 25 Sept 02:05 UTC: the « my orders » call reached the database;
// the « who am I » call never did.
//
// The rule now: a person's role is what the server says. A slow or failing
// server is retried; a rejected token is refreshed once, then it is a sign-out;
// a server that stays unreachable is shown as exactly that. Nobody is ever
// given the customer door because we could not ask.
//
// Pure. Guard: profileLoad.test.ts

export interface LoadedProfile {
  id: string;
  full_name: string;
  phone: string | null;
  role: 'customer' | 'admin' | 'staff';
  /** Night mode, per person (migration 209) — lib/theme/adminTheme.ts. */
  theme_pref?: string | null;
}

export type ProfileOutcome =
  | { kind: 'ok'; profile: LoadedProfile }
  | { kind: 'signed_out'; reason?: string }
  | { kind: 'unreachable' };

/** Pauses between attempts: ~7 s in all before saying « unreachable ». */
export const PROFILE_BACKOFF_MS = [500, 1500, 3000, 2000];

interface Reply { status: number; ok: boolean; json: () => Promise<unknown> }

export async function loadProfile(deps: {
  token: string;
  /** GET /auth/me with this token. May throw (network). */
  fetchMe: (token: string) => Promise<Reply>;
  /** Refresh the session; the new access token, or null when there is no session. */
  refresh: () => Promise<string | null>;
  sleep: (ms: number) => Promise<void>;
}): Promise<ProfileOutcome> {
  let token = deps.token;
  let refreshed = false;
  for (let attempt = 0; attempt <= PROFILE_BACKOFF_MS.length; attempt += 1) {
    try {
      const r = await deps.fetchMe(token);
      if (r.ok) {
        const body = await r.json().catch(() => null) as { profile?: LoadedProfile } | null;
        if (body?.profile?.role) return { kind: 'ok', profile: body.profile };
      } else if (r.status === 401) {
        // The login gate said no (not on the list, password login off, logged
        // out, older than 30 days — worker lib/authGate.ts): a refresh cannot
        // change that. Out, with the reason for the login page (2026-10-21).
        const body = await r.json().catch(() => null) as { reason?: string } | null;
        if (body?.reason && body.reason !== 'invalid') return { kind: 'signed_out', reason: body.reason };
        // The token was refused. One refresh; if that does not fix it, the
        // session is dead — the login page, not a guessed role.
        if (refreshed) return { kind: 'signed_out' };
        refreshed = true;
        const next = await deps.refresh().catch(() => null);
        if (!next) return { kind: 'signed_out' };
        token = next;
        continue;
      }
      // 5xx, or a 200 with no profile: the server had a bad moment — try again.
    } catch {
      // Could not reach the server — try again.
    }
    if (attempt < PROFILE_BACKOFF_MS.length) await deps.sleep(PROFILE_BACKOFF_MS[attempt]);
  }
  return { kind: 'unreachable' };
}

export type Entry = { go: string } | 'customer' | 'wait' | 'error' | 'anonymous';

/**
 * Which door. The team goes to the admin (admin → the dashboard, staff → the
 * orders, their daily home); a customer to « Mes commandes ». ⚠️ A signed-in
 * person whose profile is not known yet WAITS — the customer door is only ever
 * for someone the server said is a customer.
 */
export function entryFor(s: {
  user: unknown | null;
  profile: { role: string } | null;
  profileError: string | null;
}): Entry {
  if (!s.user) return 'anonymous';
  if (!s.profile) return s.profileError ? 'error' : 'wait';
  if (s.profile.role === 'admin') return { go: '/admin' };
  if (s.profile.role === 'staff') return { go: '/admin/orders' };
  return 'customer';
}
